import { useLazyQuery } from "@apollo/client/react";
import { Button, Stack, Typography } from "@mui/material";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CREDENTIAL_TYPES, CREDENTIAL_TYPES_BY_NAME, IfPermission } from "../../../components";
import { FormSubmitButton } from "../../../components/KeyEditor/KeyEditorCommon";
import { EditorButtonType } from "../../../components/KeyEditor/KeyEditorTypes";
import Permissions from "../../../constants/permissions";
import { useAccountContext } from "../../../strict/app/AccountResolver";
import { Credential, StartOauthDocument } from "../../../graphql/types";
import type { StartOauthQuery, StartOauthQueryVariables } from "../../../graphql/types";

import { PencilSimpleLineIcon } from "@phosphor-icons/react/dist/csr/PencilSimpleLine";
import { TrashIcon } from "@phosphor-icons/react/dist/csr/Trash";

import { toast } from "react-toastify";

type KeyInformationFooterProps = {
  onClickDelete: () => void;
  onClickEditKey: () => void;
  credential: Credential;
};

type ButtonTypeKeys = Extract<
  keyof typeof CREDENTIAL_TYPES,
  "facebook" | "google_cloud_platform_token_2023" | "youtube_token_2023"
>;

const BUTTON_TYPE: { [keyname in ButtonTypeKeys]: EditorButtonType } = {
  facebook: "facebook",
  google_cloud_platform_token_2023: "google",
  youtube_token_2023: "youtube",
};

const KeyInformationFooter = ({ onClickDelete, onClickEditKey, credential }: KeyInformationFooterProps) => {
  const [getOauthURL, { loading }] = useLazyQuery<StartOauthQuery, StartOauthQueryVariables>(StartOauthDocument, {
    errorPolicy: "all",
    fetchPolicy: "no-cache",
  });

  const { urlName: url_name } = useAccountContext();

  const navigate = useNavigate();

  useEffect(() => {
    const handleOauthSuccess = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data.message === "oauthSuccess") {
        toast.success(`${event.data.credential_name} was successfully updated!`);

        navigate(`/app/${url_name}/keys`);
      }
    };

    window.addEventListener("message", handleOauthSuccess);

    return () => {
      window.removeEventListener("message", handleOauthSuccess);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isOauth = CREDENTIAL_TYPES_BY_NAME[credential.credential_type]?.isOauth;

  const onSubmit = async () => {
    const { data } = await getOauthURL({
      variables: {
        name: credential.name,
        oauthType: credential.credential_type || "",
        entity: "credential",
        ...(credential.id ? { id: credential.id.toString() } : {}),
      },
    });
    if (data?.start_oauth) {
      window.open(data.start_oauth, "oauthpop", "width=800, height=600");
    }
  };

  const hasBrandingGuidelines = credential.credential_type in BUTTON_TYPE;

  return (
    <IfPermission permission={Permissions.ManageKeys}>
      <Stack direction="row" sx={{ justifyContent: "space-between", width: "100%", alignItems: "flex-end" }}>
        <Button onClick={onClickDelete} variant="text" color="secondary" startIcon={<TrashIcon size={24} />}>
          <Typography variant="button.large">Delete Key</Typography>
        </Button>
        <Stack direction={hasBrandingGuidelines ? "row" : "column"} sx={{ alignItems: "flex-end", marginTop: "40px" }}>
          <Button
            color="secondary"
            sx={{
              padding: "12px 24px 12px 24px",
              marginRight: isOauth && hasBrandingGuidelines ? "10px" : undefined,
              marginBottom: isOauth && !hasBrandingGuidelines ? "10px" : undefined,
            }}
            variant={isOauth ? "outlined" : undefined}
            endIcon={<PencilSimpleLineIcon size={24} />}
            onClick={onClickEditKey}
          >
            <Typography variant="button.large" sx={{ color: isOauth ? "secondary60.main" : "text10.main" }}>{`Edit key${
              isOauth ? " name" : ""
            }`}</Typography>
          </Button>
          {isOauth && (
            <form onSubmit={onSubmit}>
              <FormSubmitButton
                isSubmitting={false}
                loading={loading || false}
                isValid={true}
                buttonType={BUTTON_TYPE[credential.credential_type as ButtonTypeKeys] || undefined}
                buttonText="Reconnect"
              />
            </form>
          )}
        </Stack>
      </Stack>
    </IfPermission>
  );
};

export { KeyInformationFooter };
